import { encodeUrl } from './encodeUrl';

/**
 * Estado local do fluxo /match:
 * - rascunho das respostas (visitante responde sem login; envio só após entrar/criar conta);
 * - flag de retorno para /match após login/cadastro/OAuth.
 *
 * O RESULTADO do match não mora aqui: vem do servidor por conta logada. A chave
 * antiga só é lida para ser apagada (ver clearMatchResultLocally).
 */
const MATCH_DRAFT_KEY = 'plumaa_match_draft';
const MATCH_RETURN_PENDING_KEY = 'plumaa_match_return_pending';
const MATCH_RESULT_KEY = 'plumaa_match_result';

export interface MatchDraft {
	form_id: string;
	answers: Record<string, string | null>;
	/** true quando o usuário chegou ao fim do questionário (falta só entrar/criar conta e enviar). */
	completed: boolean;
	updated_at: string;
}

function safeGet(key: string): string | null {
	try {
		return localStorage.getItem(key);
	} catch {
		return null;
	}
}

function safeSet(key: string, value: string): void {
	try {
		localStorage.setItem(key, value);
	} catch {
		/* ignore */
	}
}

function safeRemove(key: string): void {
	try {
		localStorage.removeItem(key);
	} catch {
		/* ignore */
	}
}

export function saveMatchDraft(draft: Omit<MatchDraft, 'updated_at'>): void {
	safeSet(
		MATCH_DRAFT_KEY,
		JSON.stringify({ ...draft, updated_at: new Date().toISOString() }),
	);
}

export function readMatchDraft(): MatchDraft | null {
	const raw = safeGet(MATCH_DRAFT_KEY);
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw) as Partial<MatchDraft>;
		if (
			typeof parsed.form_id !== 'string' ||
			typeof parsed.answers !== 'object' ||
			parsed.answers === null
		) {
			return null;
		}
		return {
			form_id: parsed.form_id,
			answers: parsed.answers,
			completed: Boolean(parsed.completed),
			updated_at: parsed.updated_at ?? new Date().toISOString(),
		};
	} catch {
		return null;
	}
}

export function clearMatchDraft(): void {
	safeRemove(MATCH_DRAFT_KEY);
}

/** Chamar antes de mandar o visitante para /login, /cadastro ou OAuth a partir do /match. */
export function markMatchReturnPending(): void {
	safeSet(MATCH_RETURN_PENDING_KEY, 'true');
}

export function isMatchReturnPending(): boolean {
	return safeGet(MATCH_RETURN_PENDING_KEY) === 'true';
}

export function clearMatchReturnPending(): void {
	safeRemove(MATCH_RETURN_PENDING_KEY);
}

/** Consome a flag: true se o pós-auth deve voltar para /match. */
export function consumeMatchReturnPending(): boolean {
	const pending = isMatchReturnPending();
	if (pending) clearMatchReturnPending();
	return pending;
}

export const MATCH_PAGE_PATH = '/match' as const;

/**
 * Apaga o resultado do match que versões anteriores guardavam no browser.
 * Hoje os indicados do usuário vêm do servidor (`GET /match/me`, hook
 * `useMatchedProfessionalRanks`): match é da conta, não do dispositivo.
 */
export function clearMatchResultLocally(): void {
	safeRemove(MATCH_RESULT_KEY);
}

/** Entra no agendamento padrão já no passo de horário do profissional escolhido. */
/** Caminho do agendamento no passo de horário; `startTime` (ISO) pré-seleciona o slot. */
export function buildMatchSchedulingPath(
	professionalId: string,
	startTime?: string,
): string {
	const payload: Record<string, string | number> = {
		s: 2,
		p: professionalId,
	};
	if (startTime) payload.t = startTime;
	return `/agendamento?d=${encodeUrl(payload)}`;
}

export function formatMatchRankLabel(rank: number): string {
	return `${rank}º match`;
}
